import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import { ArrowRight, Shield, Building2, Home, Check, Phone, Star, Calendar, Zap, Bug, Hotel, LayoutDashboard } from "lucide-react";
import SEO from "../components/SEO";
import { useAuth } from "../contexts/AuthContext";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const AnimatedCounter = ({ target, suffix = "", format = false, duration = 2 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const motionVal = useMotionValue(0);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (isInView) {
      const controls = animate(motionVal, target, {
        duration,
        ease: [0.25, 0.46, 0.45, 0.94],
        onUpdate: (v) => {
          const rounded = Math.round(v);
          setDisplay(format ? rounded.toLocaleString("fr-FR") : String(rounded));
        },
      });
      return controls.stop;
    }
  }, [isInView, target, motionVal, duration, format]);

  return <span ref={ref}>{display}{suffix}</span>;
};

const HomeQualifier = () => {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const dashboardPath = isAdmin ? "/admin" : "/dashboard";
  return (
    <div className="min-h-screen bg-white font-['Manrope'] overflow-hidden">
      <SEO page="home" />

      {/* ===== HEADER — fixed dark glass ===== */}
      <motion.header
        className="fixed w-full z-50 backdrop-blur-xl bg-[#19294C]/90 border-b border-white/10"
        data-testid="qualifier-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto brightness-0 invert" />
            </Link>
            <div className="flex items-center gap-3 md:gap-5">
              <a
                href="tel:+33756868432"
                className="inline-flex items-center gap-1.5 text-sm text-white/70 font-medium hover:text-[#00B8E4] transition-colors"
                aria-label="Appelez-nous au 07 56 86 84 32"
                data-testid="header-phone-btn"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline">07 56 86 84 32</span>
              </a>
              {user ? (
                <Link
                  to={dashboardPath}
                  className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors font-medium"
                  data-testid="qualifier-dashboard-btn"
                >
                  <LayoutDashboard className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Tableau de bord</span>
                  <span className="sm:hidden">Dashboard</span>
                </Link>
              ) : (
                <Link
                  to="/connexion"
                  className="text-sm text-white/60 hover:text-white transition-colors font-medium"
                  data-testid="qualifier-login-btn"
                >
                  Connexion
                </Link>
              )}
              <a
                href="https://api.leadconnectorhq.com/widget/booking/AK3IH1nwEwNhiDkL4Iak"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button
                  className="bg-[#00B8E4] text-white text-sm px-5 py-2.5 rounded-full hover:bg-[#00a5ce] transition-colors font-semibold shadow-sm shadow-[#00B8E4]/20"
                  data-testid="header-cta-btn"
                >
                  <span className="hidden sm:inline">Prendre rendez-vous</span>
                  <span className="sm:hidden"><Calendar className="w-4 h-4" /></span>
                </button>
              </a>
            </div>
          </div>
        </div>
      </motion.header>

      {/* ===== HERO — Split choice ===== */}
      <section
        className="relative min-h-[92vh] pt-28 md:pt-32 pb-16 md:pb-20 flex flex-col items-center justify-center bg-[#19294C] overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background texture */}
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] bg-cover bg-center opacity-[0.07]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#19294C] via-transparent to-[#19294C]" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          {/* Trust pill */}
          <motion.div
            className="inline-flex items-center gap-2 bg-[#00B8E4]/10 border border-[#00B8E4]/25 rounded-full px-5 py-2 mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            data-testid="trust-badge"
          >
            <Shield className="w-4 h-4 text-[#00B8E4]" />
            <span className="text-xs font-bold text-[#00B8E4] tracking-wide">
              N°1 DE L'ASSURANCE LOCATION COURTE DURÉE
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-['Outfit'] tracking-tighter leading-[1.1]"
            data-testid="qualifier-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            L'assurance qui sécurise
            <br />
            <span className="bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] bg-clip-text text-transparent">
              vraiment vos revenus locatifs
            </span>
          </motion.h1>

          <motion.p
            className="mt-5 text-base md:text-lg text-white/50 leading-relaxed max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            Vandalisme, punaises de lit, perte d'exploitation — remboursement sous 48h.
          </motion.p>

          {/* ===== CHOICE CARDS ===== */}
          <motion.div
            className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto"
            initial="hidden"
            animate="visible"
            variants={stagger}
          >
            {/* Particulier */}
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <Link
                to="/particulier"
                className="group relative block rounded-3xl overflow-hidden bg-white/[0.04] backdrop-blur-xl border border-white/15 hover:border-[#00B8E4]/60 transition-all duration-500 hover:shadow-[0_0_60px_-15px_rgba(0,184,228,0.25)]"
                data-testid="qualifier-card-particular"
              >
                {/* Card image */}
                <div className="relative h-44 md:h-52 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1713832139677-7fefa9ee3df0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjb3p5JTIwYnJpZ2h0JTIwcGFyaXNpYW4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzc1NTg0MzkzfDA&ixlib=rb-4.1.0&q=85"
                    alt="Appartement cosy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#19294C] via-[#19294C]/50 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <div className="w-11 h-11 rounded-xl bg-[#00B8E4]/15 backdrop-blur-md border border-[#00B8E4]/30 flex items-center justify-center">
                      <Home className="w-5 h-5 text-[#00B8E4]" />
                    </div>
                  </div>
                </div>

                {/* Card content */}
                <div className="p-6 md:p-7">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00B8E4] mb-1.5">
                    PROPRIÉTAIRE / RÉSIDENCE SECONDAIRE
                  </p>
                  <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] tracking-tight">
                    Je suis un Particulier
                  </h3>
                  <p className="mt-2 text-white/50 text-sm leading-relaxed">
                    Je loue mon propre bien sur Airbnb, Booking ou Abritel.
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[#00B8E4] text-sm font-semibold group-hover:gap-3 transition-all">
                    Voir mon offre
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </motion.div>

            {/* Professionnel */}
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <Link
                to="/professionnel"
                className="group relative block rounded-3xl overflow-hidden bg-white/[0.04] backdrop-blur-xl border border-white/15 hover:border-[#00B8E4]/60 transition-all duration-500 hover:shadow-[0_0_60px_-15px_rgba(0,184,228,0.25)]"
                data-testid="qualifier-card-pro"
              >
                {/* Card image */}
                <div className="relative h-44 md:h-52 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1758193431355-54df41421657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwYnVpbGRpbmclMjBmYWNhZGV8ZW58MHx8fHwxNzc1NTg0MzkzfDA&ixlib=rb-4.1.0&q=85"
                    alt="Immeuble moderne"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#19294C] via-[#19294C]/50 to-transparent" />
                  <div className="absolute top-5 left-5">
                    <div className="w-11 h-11 rounded-xl bg-[#00B8E4]/15 backdrop-blur-md border border-[#00B8E4]/30 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-[#00B8E4]" />
                    </div>
                  </div>
                  {/* Popular badge */}
                  <div className="absolute top-5 right-5 bg-[#00B8E4] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                    Populaire
                  </div>
                </div>

                {/* Card content */}
                <div className="p-6 md:p-7">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00B8E4] mb-1.5">
                    GESTIONNAIRE / CONCIERGERIE / AGENCE
                  </p>
                  <h3 className="text-2xl md:text-3xl font-bold text-white font-['Outfit'] tracking-tight">
                    Je suis un Professionnel
                  </h3>
                  <p className="mt-2 text-white/50 text-sm leading-relaxed">
                    Je gère plusieurs logements en location courte durée.
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[#00B8E4] text-sm font-semibold group-hover:gap-3 transition-all">
                    Voir l'offre Pro
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </motion.div>
          </motion.div>

          {/* Phone CTA — frictionless path for visitors who'd rather call */}
          <motion.div
            className="mt-12 md:mt-14 text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            data-testid="hero-phone-block"
          >
            <div className="inline-flex items-center gap-3 text-[11px] font-bold text-white/40 tracking-[0.2em] uppercase mb-3">
              <span className="block w-8 h-px bg-white/20" />
              Ou
              <span className="block w-8 h-px bg-white/20" />
            </div>
            <p className="text-white/60 text-sm md:text-base mb-4">
              Une question ? Parlez à un conseiller HostProtect.
            </p>
            <a
              href="tel:+33756868432"
              className="inline-flex items-center gap-3 bg-white/[0.08] backdrop-blur-xl border border-white/20 hover:border-[#00B8E4]/60 hover:bg-white/[0.12] rounded-full px-5 sm:px-6 py-3 transition-all duration-300"
              data-testid="hero-phone-cta"
              aria-label="Appelez-nous au 07 56 86 84 32"
            >
              <span className="w-9 h-9 rounded-full bg-[#00B8E4] flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-white" />
              </span>
              <span className="font-bold text-white text-base md:text-lg font-['Outfit'] tracking-tight">
                07 56 86 84 32
              </span>
              <span className="hidden sm:inline text-xs text-white/45 pl-1">
                Lun-Ven · 9h-18h
              </span>
            </a>
          </motion.div>

          <motion.p
            className="mt-8 text-xs text-white/25"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.9 }}
            data-testid="not-eligible-link"
          >
            Ni l'un ni l'autre ?{" "}
            <Link to="/not-eligible" className="underline text-white/40 hover:text-[#00B8E4] transition-colors">
              Liste d'attente
            </Link>
          </motion.p>
        </div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="py-14 md:py-16 bg-white border-b border-slate-200" data-testid="trust-bar">
        <motion.div
          className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={stagger}
        >
          {[
            { target: 48, suffix: "h", label: "Remboursement max" },
            { target: 98, suffix: "%", label: "Sinistres réglés" },
            { target: 10000, suffix: "+", label: "Logements assurés", format: true },
            { target: 50000, suffix: "€", label: "Couverture max", format: true },
          ].map((stat, i) => (
            <motion.div key={i} className="text-center" variants={fadeUp} transition={{ duration: 0.4 }}>
              <p className="text-3xl md:text-4xl font-black text-[#19294C] font-['Outfit']" data-testid={`stat-${i}`}>
                <AnimatedCounter target={stat.target} suffix={stat.suffix} format={stat.format} duration={stat.target > 1000 ? 2.2 : 1.6} />
              </p>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mt-2">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ===== COVERAGE BENTO ===== */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-[#F8FAFC]" data-testid="coverage-section">
        <motion.div
          className="max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div className="text-center mb-14" variants={fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-xs font-bold text-[#00B8E4] tracking-[0.2em] uppercase mb-3">Couvertures exclusives</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#19294C] font-['Outfit'] tracking-tight" data-testid="coverage-title">
              Ce que les plateformes ne couvrent pas
            </h2>
            <p className="mt-3 text-slate-500 max-w-lg mx-auto">
              Airbnb, Booking, Abritel… aucune ne vous protège réellement. HostProtect prend le relais.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Zap, title: "Vandalisme", amount: "50 000€", desc: "Dégâts causés par les voyageurs sur vos biens mobiliers", color: "from-[#00B8E4]/10 to-transparent" },
              { icon: Bug, title: "Punaises de lit", amount: "10 000€", desc: "Traitement, désinfection et relogement temporaire", color: "from-[#2A63C5]/10 to-transparent" },
              { icon: Shield, title: "Perte d'exploitation", amount: "2 semaines", desc: "Revenus protégés si le logement est inutilisable", color: "from-[#00B8E4]/10 to-transparent" },
              { icon: Hotel, title: "Relogement", amount: "1 000€", desc: "Hôtel et repas pour vos voyageurs en cas de sinistre", color: "from-[#2A63C5]/10 to-transparent" },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl p-6 border border-slate-200/80 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                variants={fadeUp}
                transition={{ duration: 0.4 }}
                data-testid={`coverage-card-${i}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <item.icon className="w-5 h-5 text-[#00B8E4]" />
                </div>
                <p className="text-2xl font-black text-[#19294C] font-['Outfit']">{item.amount}</p>
                <h3 className="text-base font-bold text-[#19294C] font-['Outfit'] mt-1">{item.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* Franchise note */}
          <motion.p
            className="mt-8 text-center text-sm text-slate-400"
            variants={fadeUp}
            transition={{ duration: 0.4 }}
          >
            Franchise de <strong className="text-[#19294C]">10% du sinistre (min. 150€)</strong> par sinistre — aucune vétusté appliquée.
          </motion.p>
        </motion.div>
      </section>

      {/* ===== WHY HOSTPROTECT — Checklist ===== */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white" data-testid="why-section">
        <motion.div
          className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
        >
          <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-xs font-bold text-[#00B8E4] tracking-[0.2em] uppercase mb-3">Pourquoi HostProtect</p>
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#19294C] font-['Outfit'] tracking-tight leading-tight"
              data-testid="why-title"
            >
              La confiance de 10 000+
              <br />
              propriétaires en France
            </h2>
            <div className="mt-8 space-y-4">
              {[
                "Remboursement garanti sous 48h maximum",
                "Franchise de 10% du sinistre (min. 150€) — aucune surprise",
                "Punaises de lit couvertes — exclusivité marché",
                "Aucune vétusté appliquée sur les remboursements",
                "Souscription en 2 minutes, résiliation libre",
                "Assureur agréé Mutuaide Assistance — fonds garantis",
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-3"
                  variants={fadeUp}
                  transition={{ duration: 0.3 }}
                >
                  <div className="w-6 h-6 rounded-full bg-[#00B8E4] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </div>
                  <p className="text-sm text-[#19294C] font-medium">{item}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Testimonial card */}
          <motion.div variants={fadeUp} transition={{ duration: 0.6 }}>
            <div className="bg-[#F8FAFC] rounded-3xl p-8 md:p-10 border border-slate-200/80 relative">
              <div className="flex gap-0.5 mb-5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <blockquote
                className="text-lg md:text-xl text-[#19294C] font-medium leading-relaxed"
                data-testid="home-testimonial"
              >
                "Depuis que j'utilise HostProtect, je dors sur mes deux oreilles. Un sinistre de 4 500€ remboursé en 48h. Je recommande à tous les gestionnaires."
              </blockquote>
              <div className="mt-6 flex items-center gap-4">
                <img
                  src="https://images.unsplash.com/photo-1758518729466-827cd8293992?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwcm9wZXJ0eSUyMG1hbmFnZXIlMjB3b21hbiUyMHBvcnRyYWl0JTIwc21pbGluZ3xlbnwwfHx8fDE3NzU1ODQzOTN8MA&ixlib=rb-4.1.0&q=85"
                  alt="Marie L."
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-[#00B8E4]/20"
                />
                <div>
                  <p className="font-bold text-[#19294C] text-sm">Marie L.</p>
                  <p className="text-xs text-slate-500">Gestionnaire à Paris — 12 logements</p>
                </div>
              </div>
              {/* Quote mark decoration */}
              <div className="absolute top-6 right-8 text-7xl font-['Outfit'] text-slate-200 leading-none select-none">"</div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== PRICING CTA ===== */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 bg-[#19294C] relative overflow-hidden"
        data-testid="final-cta-section"
      >
        {/* Gradient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00B8E4]/[0.06] rounded-full blur-[120px] pointer-events-none" />

        <motion.div
          className="max-w-3xl mx-auto text-center relative z-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold text-[#00B8E4] tracking-[0.2em] uppercase mb-4">Choisissez votre profil</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] tracking-tight">
            Protégez vos logements maintenant
          </h2>

          {/* Dual choice block */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <Link to="/professionnel" className="group">
              <div className="bg-white/[0.06] border border-white/15 rounded-2xl p-6 hover:border-[#00B8E4]/50 transition-all text-center group-hover:bg-white/[0.08]">
                <p className="text-base font-bold text-white tracking-wide mb-2 font-['Outfit']">Professionnel</p>
                <p className="text-xs text-white/50 mt-1">Conciergeries et gestionnaires</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[#00B8E4] text-sm font-semibold group-hover:gap-2.5 transition-all">
                  Découvrir <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
            <Link to="/particulier" className="group">
              <div className="bg-white/[0.06] border border-white/15 rounded-2xl p-6 hover:border-[#00B8E4]/50 transition-all text-center group-hover:bg-white/[0.08]">
                <p className="text-base font-bold text-white tracking-wide mb-2 font-['Outfit']">Particulier</p>
                <p className="text-xs text-white/50 mt-1">Propriétaires bailleurs</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-[#00B8E4] text-sm font-semibold group-hover:gap-2.5 transition-all">
                  Découvrir <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/inscription">
              <button
                className="bg-gradient-to-r from-[#00B8E4] to-[#2A63C5] text-white px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all inline-flex items-center gap-2 text-base shadow-lg shadow-[#00B8E4]/25 hover:-translate-y-0.5"
                data-testid="final-cta-btn"
              >
                Souscrire maintenant
                <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <a
              href="https://api.leadconnectorhq.com/widget/booking/AK3IH1nwEwNhiDkL4Iak"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="border border-white/20 text-white px-8 py-4 rounded-full font-semibold hover:bg-white/5 transition-all inline-flex items-center gap-2 text-base hover:-translate-y-0.5">
                <Phone className="w-4 h-4" />
                Prendre rendez-vous
              </button>
            </a>
          </div>

          <p className="mt-6 text-xs text-white/30">Souscription en 2 min · Sans engagement · Résiliation libre</p>
        </motion.div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-10 px-6 md:px-12 bg-[#0B1221] border-t border-white/5" data-testid="home-footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <img src="/logo-hostprotect.png" alt="HostProtect" className="h-7 w-auto brightness-0 invert opacity-60" />
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <a href="tel:+33756868432" className="hover:text-white transition-colors">07 56 86 84 32</a>
            <a href="mailto:contact@hostprotect.io" className="hover:text-white transition-colors">contact@hostprotect.io</a>
            <Link to="/connexion" className="hover:text-white transition-colors">Espace client</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-6 pt-4 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-slate-600">© {new Date().getFullYear()} HostProtect — PM Hospitality SAS</p>
          <div className="flex items-center gap-4 text-xs text-slate-600">
            <Link to="/conditions-generales-de-vente" className="hover:text-slate-400 transition-colors" data-testid="home-footer-cgv">CGV</Link>
            <Link to="/politique-de-confidentialite" className="hover:text-slate-400 transition-colors" data-testid="home-footer-privacy">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeQualifier;
